/**
 * adaptivePaths — the single source of truth for which logged-out marketing
 * pages are SYSTEM-ADAPTIVE (they follow the visitor's OS theme via
 * html[data-mkt-theme] + MarketingAdaptiveTheme.css) instead of dark-locked.
 *
 * Batch 1 (Nitish, Aug 23 pm): Home + the seven core marketing pages.
 * Batch 1 follow-on (CEL-1627, Aug 23): /login + /signup join the list —
 * their SHELL rides it like any other page, and their form INTERNALS flip
 * via ThemeContext (see ADAPTIVE_AUTH below), which resolves the visitor's
 * real preference/OS theme on these paths instead of the logged-out dark
 * pin, so antd + the auth [data-theme] CSS render the same face as the
 * shell.
 * Batch 2 (Nitish, Aug 23 eve): the FULL remaining marketing surface —
 * compare pages, role + vertical pages (the /ai-employees/ prefix),
 * /benchmarks, /ai-organization, /producthunt, /podcast (+ episodes), and
 * the integration spokes (/openclaw, /linear, /hermes, /cursor,
 * /claude-code, /codex). /forgot-password stays the one dark-locked
 * exclusion (auth internals ruling, CEL-1627).
 *
 * Consumed by MarketingLayout (applies .mkt-adaptive to the shell),
 * CookieBanner (drops its dark skin on the light face) AND ThemeContext
 * (auth paths only) — one list, no drift.
 */
const ADAPTIVE_EXACT = new Set([
  '/',
  '/ai-employees',
  '/super-agents',
  '/agent-to-agent',
  '/pricing',
  '/story',
  '/security',
  '/brand',
  '/contact',
  '/tutorials',
  '/login',
  '/signup',
  '/benchmarks',
  '/ai-organization',
  '/producthunt',
  '/compare',
  '/podcast',
  '/openclaw',
  '/linear',
  '/hermes',
  '/cursor',
  '/claude-code',
  '/codex',
  '/posts',
  '/skills',
  '/support',
  '/for-agents', // Sep 7 2026 (Nitish): page had light CSS since CEL-2097 but was never listed, so it stayed dark-locked
  '/book-a-demo', // Oct 22 2026 (CEL-3640)
  '/tools', // Nov 3 2026 (the public Tools page)
]);

/* Slug routes under an adaptive hub (e.g. /tutorials/hire-your-first-employee,
   /compare/grok-bot, /ai-employees/ai-sdr, /podcast/episode-1,
   /support/terms-of-service, /skills/<slug>, /posts/<id>) inherit the
   hub's adaptive treatment. */
const ADAPTIVE_PREFIXES = ['/tutorials/', '/compare/', '/podcast/', '/ai-employees/', '/posts/', '/skills/', '/support/'];

/* Adaptive paths whose page INTERNALS are themed by the APP theme (antd
   algorithm + [data-theme] CSS) rather than marketing CSS alone: the auth
   pages. On THESE paths ThemeContext lets a logged-out visitor's resolved
   theme follow the same inputs as html[data-mkt-theme] (explicit preference
   wins, else the OS) instead of pinning dark. ThemeContext stays the single
   writer of data-theme (CEL-1359) — this list is just one of its inputs. */
const ADAPTIVE_AUTH = new Set(['/login', '/signup']);

/* Batch 3 (Sep 7 2026): the same ThemeContext treatment for the app-themed
   public pages — their internals are keyed on isDarkMode (own .light/.dark
   CSS), so resolving the visitor's real theme here IS their light face. */
const ADAPTIVE_APP_EXACT = new Set(['/posts', '/skills', '/support']);
const ADAPTIVE_APP_PREFIXES = ['/posts/', '/skills/', '/support/'];

/* Routes that also exist as a public/ directory (e.g. /for-agents holds its .md alternate at
   public/for-agents/index.md) are served with a trailing slash ("/for-agents/"), which an exact
   Set lookup misses: the page then renders dark-locked on a light OS (Sep 7 2026). Normalize.
   Exported for other callers that need the same rule. */
export const normalizePath = (pathname) =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

export const isAdaptivePath = (rawPathname) => {
  const pathname = normalizePath(rawPathname);
  return ADAPTIVE_EXACT.has(pathname) || ADAPTIVE_PREFIXES.some((p) => pathname.startsWith(p));
};

/* Name kept for its two consumers (ThemeContext + the App.js bridge): "the
   logged-out paths where the APP theme follows the visitor". Auth pages since
   CEL-1627; the footer's app-themed pages since Sep 7 2026. */
export const isAdaptiveAuthPath = (rawPathname) => {
  const pathname = normalizePath(rawPathname);
  return ADAPTIVE_AUTH.has(pathname) ||
  ADAPTIVE_APP_EXACT.has(pathname) ||
  ADAPTIVE_APP_PREFIXES.some((p) => pathname.startsWith(p));
};
